/**
 * ViewerApp — the viewer's composition entry. Resolves where the workspace
 * comes from (share link > ?src= > persisted upload > demo; server in preview;
 * a host snapshot when controlled), mounts the workspace service chain
 * (`ViewerProviders`), and renders `ViewerComposition`. All the logic lives in
 * `useViewerController`; all the markup in `ViewerComposition`. This file is
 * just boot + providers — the strangler-fig is complete (the old App/ViewerShell
 * god-components are gone).
 */

import { useState } from "react";
import { ToastProvider } from "@archlang/ui";
import { useTranslation } from "./i18n";
import {
  loadPersistedViewerState,
  type PersistedViewerState,
} from "./viewerStorage";
import { storageGeneration } from "./host/hostStorage";
import type { Vfs } from "@archlang/viewer-core";
import { type SourceMode } from "./sources/useSources";
import {
  resolveViewerFileSource,
  type ViewerBootSource,
} from "./sources/viewerFileSource";
import { ViewerProviders } from "./workbench/ViewerProviders";
import type { FileSource } from "./workbench/filesource/types";
import { createSnapshotFileSource } from "./workbench/filesource/snapshot";
import { ViewerComposition } from "./workbench/ViewerComposition";
import type { CreateWorker } from "./workbench/monaco/lspBridge";
import type { TabSpec } from "./workbench/tabs";
import type { ViewerSurface } from "./workbench/surface";
import { readAspectPrefs } from "./hooks/useAspectOverlay";
import { registerBuiltinLanguagePacks } from "./workbench/languagePacks/builtins";

// Register the built-in light language packs' inspector controls (the SLO pack's
// `slo.*` / `observed.*` numeric editor) once at module load, before first
// render. Inert until a matching aspect key is present. §language packs.
registerBuiltinLanguagePacks();

let bootState: { generation: number; value: PersistedViewerState | null } | null =
  null;

/**
 * Viewer state persisted by a previous session, read ONCE — but LAZILY.
 *
 * Lazily is the load-bearing word. A host that owns durable storage can only
 * install its backend after `arch:init` brings it the seed, which is after the
 * bundle is already running — so a module-load read runs first, hits the
 * default store, and hands back defaults. In a webview, whose origin is
 * per-instance, that means the camera resets on every single open, silently.
 *
 * Cached after the first call, because subsequent state changes go through the
 * debounced save; re-reading would feed live state back in as the restore
 * point. Keyed by {@link storageGeneration} so installing a different backend
 * invalidates a snapshot taken from the previous one. A share link's view
 * (resolved per mount in `resolveAppBoot`) overrides whatever this returns.
 */
export function persistedViewerStateAtBoot(): PersistedViewerState | null {
  const generation = storageGeneration();
  if (!bootState || bootState.generation !== generation) {
    bootState = {
      generation,
      value: typeof window !== "undefined" ? loadPersistedViewerState() : null,
    };
  }
  return bootState.value;
}

// Save side: localStorage write per state change is wasteful (zoom +
// drag emit dozens of changes per second). Debounce to a comfortable
// idle so the persisted snapshot is "your last paused state."

interface SourceSlot {
  vfs: Vfs;
  label: string;
}

export interface ViewerAppProps {
  /**
   * Which surface is mounting the app. Drives source-acquisition:
   * "viewer" enables upload + persistence + demo fallback; "preview"
   * fetches `/api/files` from a backing server and live-updates via
   * `/api/events`. Defaults to "viewer" so anyone embedding without
   * specifying a mode gets the safe (no-server-required) behaviour.
   */
  readonly mode?: SourceMode;
  /**
   * Mount on a caller-built `FileSource`, skipping source acquisition entirely
   * (share link / `?src=` / upload / demo / server). This is how a native host
   * drives the app over its own transport — see `workbench/filesource/bridge.ts`.
   * Unlike `controlledSources` (a host-owned VFS *snapshot* the host keeps
   * pushing), a source is live: it loads, watches, and — if it implements the
   * write methods — takes edits back.
   */
  readonly source?: FileSource;
  /**
   * Drive the app from an externally-owned VFS instead of its own source
   * acquisition (upload / server / demo). When set, the source becomes
   * controlled: editing the host's files re-renders the model, and the
   * file-picker / share / persistence chrome is suppressed (the host owns
   * files). Used to embed the full viewer inside Studio's editor preview.
   */
  readonly controlledSources?: SourceSlot;
  /**
   * Hide the left workspace/navigation panel, leaving the board + breadcrumb +
   * right details sidebar. For embeds whose host already provides file
   * navigation (e.g. Studio's editor, which has its own file tree).
   */
  readonly hideWorkspacePanel?: boolean;
  /**
   * Suppress the tab strip. For a host whose own tabs already name and manage
   * this surface — a VS Code editor tab does — where drawing ours inside it
   * gives the user two tab bars for one surface.
   *
   * Note the details TOGGLE lives in this strip, so hiding the strip without
   * also setting {@link hideDetailsPanel} leaves the inspector permanently
   * open. Own both, or neither.
   */
  readonly hideTabStrip?: boolean;
  /**
   * Suppress the right details sidebar, for a host that renders the inspector
   * itself as a native dockable view. Separate from {@link hideTabStrip}: a
   * host may own one and not the other.
   */
  readonly hideDetailsPanel?: boolean;
  /**
   * Suppress the in-page code/document editor pane, for a host that IS the
   * editor (VS Code opens the file in its own editor beside the webview).
   * Without it a `file` tab mounts Monaco next to the board — a second editor
   * for the same file, inside a webview that can't even fetch its chunks.
   */
  readonly hideEditor?: boolean;
  /**
   * Switch off in-board WYSIWYG editing wholesale, for a host whose model
   * edits happen in ITS text editor (`hostChrome.wysiwyg`). Kills the
   * eye/pencil stance toggle and every board edit affordance even though the
   * source would accept writes. Separate from {@link hideEditor}: that removes
   * the in-page TEXT editor; this removes structural editing on the board.
   */
  readonly disableWysiwyg?: boolean;
  /**
   * The host's message port, for inbound signals that aren't file content —
   * today the native editor's cursor (`arch:cursor`), which this app forwards
   * into the `editorSelection` store event so the camera operator follows the
   * host editor exactly as it follows the in-page one. File traffic stays on
   * the {@link source} (a bridge FileSource wraps the same port).
   */
  readonly hostPort?: { on(handler: (message: unknown) => void): () => void };
  /**
   * Which layout the app may pick. "auto" (default) switches to the phone
   * layout — full-screen surfaces behind a bottom tab bar — below the `md`
   * breakpoint. "desktop" pins the panel layout at every width, for a host
   * where a narrow viewport means a narrow PANE (a split VS Code editor) and
   * not a phone, and whose own chrome already covers what the bottom tab bar
   * would offer.
   */
  readonly layout?: "auto" | "desktop";
  /**
   * The surface the host wants shown — a view, a process, a module, a file.
   *
   * Applied at boot AND whenever it changes, so a host with its own navigation
   * can re-point one long-lived surface instead of building a fresh viewer per
   * click. Nothing else on the transport says which surface a host means.
   * Applied once the model is loaded, since a view or process tab can only
   * resolve against a compiled model.
   */
  readonly hostTab?: TabSpec | null;
  /**
   * An entity the host wants revealed on the BOARD — its nav tree's single
   * click (`arch:select`, or `init.select` on a cold boot). Selects the
   * entity and puts the board on it, exactly like the in-page nav's single
   * click; deferred until the model has compiled. The nonce distinguishes a
   * repeat click on the same entity (re-fly) from an unchanged prop.
   */
  readonly hostReveal?: { readonly id: string; readonly nonce: number } | null;
  /**
   * Offer the view builder in its EPHEMERAL tier where editing is otherwise
   * unavailable: reconfigure the board live to reason with, with no way to
   * write the config back to `.arch`.
   *
   * For a host that runs the language server as a real process rather than a
   * web worker, so {@link createLspWorker} is legitimately absent and the whole
   * edit HUD — the view builder with it — never mounts. Composing a view costs
   * nothing and touches nothing, so it need not wait on write support.
   */
  readonly allowViewBuilder?: boolean;
  /**
   * Board selection changed — the entity id now selected, or null. For a host
   * that renders its own inspector ({@link hideDetailsPanel}) and otherwise has
   * no way to know what the diagram is showing. The id is the same address the
   * nav tree uses, so the host can resolve it without asking the diagram
   * anything further.
   */
  readonly onSelectionChange?: (entityId: string | null) => void;
  /**
   * Enable in-viewer editing: the workspace panel gains an editor toggle that
   * opens a Monaco editor + tab strip alongside the board (the viewer can edit
   * now — there is no separate editable viewer). The factory builds the LSP
   * worker that powers completion / hover / rename. Omit and the toggle is
   * hidden (a pure read-only viewer, e.g. an embed).
   */
  readonly createLspWorker?: CreateWorker;
  /** Files seeded into the LSP worker VFS (e.g. the bundled stdlib). */
  readonly lspExtras?: ReadonlyMap<string, string>;
  /**
   * Alternate surfaces this app can switch into — supplied by the composition
   * root so app-shell never imports them (the standalone viewer passes
   * `@archlang/scratch`'s surface). MUST be a module constant of fixed length:
   * each entry contributes a hook. See `workbench/surface.ts`.
   */
  readonly surfaces?: readonly ViewerSurface[];
}

const EMPTY_VIEWER_APP_PROPS: ViewerAppProps = {};

export function ViewerApp(props: ViewerAppProps = EMPTY_VIEWER_APP_PROPS) {
  // Resolve where the workspace comes from ONCE per mount (share link >
  // `?src=` > persisted upload > demo; the backing server in preview; a
  // host-owned snapshot when controlled), then let `WorkspaceProvider`
  // seed and bind a WorkspaceStore to it. Everything below reads files
  // through that store — migration step 3 of viewer↔workbench unification.
  const [boot] = useState<ViewerBootSource>(() => resolveAppBoot(props));
  // Seed the store's view state from the share link's view (or the last
  // session's persisted state): selection and viewport restore exactly
  // where the user (or the link's sender) left off. Read once — the
  // provider builds its store on first render only.
  const restored = boot.viewerState ?? persistedViewerStateAtBoot();
  const { t } = useTranslation("common");
  return (
    <ToastProvider dismissLabel={t("close")}>
    <ViewerProviders
      source={boot.source}
      overlayKey={boot.overlayKey}
      storeOptions={{
        initialSelection: {
          moduleId: restored?.selectedModuleId ?? null,
          edgeId: restored?.selectedEdgeId ?? null,
          interfaceKey: restored?.selectedInterfaceKey ?? null,
        },
        initialViewport: restored?.viewport ?? null,
        // Mode is derived from the active tab; the restored tab snapshot owns
        // the perspective, so boot on the board and let tabs (below) win.
        initialMode: "board",
        // Restore the universal-tab snapshot (open tabs + per-tab state). A
        // controlled embed lets the host own this, so only seed for the
        // standalone viewer.
        initialTabs: props.controlledSources ? undefined : (restored?.tabs ?? undefined),
        initialActiveTabId: props.controlledSources ? undefined : (restored?.activeTabId ?? null),
        // Per-tab view records (selection/aspect/process), restored alongside the
        // tab snapshot so each tab reopens on its own state.
        initialViews: props.controlledSources ? undefined : (restored?.views ?? undefined),
        initialAspect: readAspectPrefs(),
      }}
      initialAgainst={
        boot.compare
          ? { vfs: boot.compare.vfs, label: boot.compare.label }
          : null
      }
      initialSwapped={boot.compare?.swapped ?? false}
    >
      <ViewerComposition {...props} boot={boot} />
    </ViewerProviders>
    </ToastProvider>
  );
}

function resolveAppBoot({
  mode = "viewer",
  source,
  controlledSources,
}: ViewerAppProps): ViewerBootSource {
  // A caller-built source wins over every acquisition route: the host already
  // decided where files come from, so share links and `?src=` must not
  // silently shadow it.
  if (source) {
    return { source, origin: "server", viewerState: null, compare: null };
  }
  if (controlledSources) {
    // Host-owned files: seed from the current snapshot; live updates flow
    // through the controlled-sync effect in the controller.
    return {
      source: createSnapshotFileSource({
        label: controlledSources.label,
        files: controlledSources.vfs,
      }),
      origin: "server",
      viewerState: null,
      compare: null,
    };
  }
  return resolveViewerFileSource({
    mode,
    hash: typeof window !== "undefined" ? window.location.hash : "",
    search: typeof window !== "undefined" ? window.location.search : "",
    server: {
      label: "loading…",
      // Widgets are code, not data — a changed bundle needs a fresh page.
      onWidgetsChanged: () => window.location.reload(),
    },
  });
}
